import React from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  Select,
  MenuItem,
  Chip,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Controller, Control, FieldErrors, useWatch } from 'react-hook-form';
import { ConfigRegistryType, ConfigRegistryConfig } from 'types/apis/pilotInterfaces';
import { useDispatch, useSelector } from 'react-redux';
import { setAdditionalConfigData, selectAdditionalConfigData } from 'store/dataReducers/formDataSlice';
import CheckIcon from '@mui/icons-material/Check';
import { SearchableMultiSelect } from 'components/common/SearchableMultiSelect';
import { filterConfigsByGroupSelection, configDisplayRank, ConfigGroupSelection, isMultiSelectRenderType, isBooleanRenderType, isFuelKeyedMap, FUEL_TYPE_DEP_REF } from './registryUtils';
import { checkIfMapStructure, parsePossibleValues } from './dynamicConfigRegistryParsing';
import { isDynamicRegistryFieldMandatory } from './dynamicConfigRegistryValidation';

interface DynamicConfigFormProps {
  configRegistryData: ConfigRegistryType[];
  control: Control<any>;
  errors: FieldErrors<any>;
  setValue: any;
  getValues?: any;
  projectId?: string;
  onFormValuesChange?: (formValues: any) => void;
  /** Selected data source, owned by the page — narrows which configs render */
  configGroupSelection?: ConfigGroupSelection;
  /** configKeys shown as read-only text because an earlier step already answered them */
  readOnlyConfigKeys?: string[];
}

// Styled checkbox to match Studio look (square, primary border, filled on check)
const StyledCheckbox: React.FC<React.ComponentProps<typeof Checkbox>> = (props) => (
  <Checkbox
    {...props}
    disableRipple
    icon={
      <Box
        className="custom-checkbox-box"
        sx={{
          width: 16,
          height: 16,
          borderRadius: '2px',
          border: '1px solid',
          borderColor: 'primary.main',
          bgcolor: 'transparent',
        }}
      />
    }
    checkedIcon={
      <Box
        className="custom-checkbox-box"
        sx={{
          width: 16,
          height: 16,
          borderRadius: '2px',
          border: '1px solid',
          borderColor: 'primary.main',
          bgcolor: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CheckIcon sx={{ fontSize: 14, color: 'primary.contrastText' }} />
      </Box>
    }
    sx={{ p: 0.5 }}
  />
);

/** Hardcoded: `frontend_configs.supported_locale` → multiselect + searchable dropdown (registry metadata optional) */
const MULTISELECT_DROPDOWN_EXCEPTION = {
  configType: 'frontend_configs',
  configKey: 'supported_locale',
} as const;

const isFrontendSupportedLocaleField = (
  cfg: ConfigRegistryConfig | undefined,
  configType?: string
): boolean => {
  if (!cfg) return false;
  return (
    String(configType || '').toLowerCase() === MULTISELECT_DROPDOWN_EXCEPTION.configType &&
    String(cfg.configKey || '').toLowerCase() === MULTISELECT_DROPDOWN_EXCEPTION.configKey
  );
};

/** Registry fields that store multiselect values as string[] */
const isRegistryMultiselectArrayField = (
  cfg: ConfigRegistryConfig | undefined,
  configType?: string
): boolean =>
  Boolean(
    cfg && (isMultiSelectRenderType(cfg.renderType) || isFrontendSupportedLocaleField(cfg, configType))
  );

/**
 * DynamicConfigForm component that renders configuration fields based on config registry data.
 * 
 * Rendering is driven by `renderType`; for MULTI_SELECT_* types values are stored as
 * string[] in form state, Redux, and onFormValuesChange, and rendered as inline checkboxes.
 *
 * Usage:
 * <DynamicConfigForm
 *   configRegistryData={configData}
 *   control={control}
 *   errors={errors}
 *   setValue={setValue}
 *   getValues={getValues}
 *   onFormValuesChange={(formattedValues) => {
 *     // formattedValues will have arrays for multiselect fields instead of comma-separated strings
 *     console.log(formattedValues);
 *   }}
 * />
 */
export const DynamicConfigForm: React.FC<DynamicConfigFormProps> = ({
  configRegistryData,
  control,
  errors,
  setValue,
  getValues,
  projectId,
  onFormValuesChange,
  configGroupSelection = {},
  readOnlyConfigKeys,
}) => {
  const dispatch = useDispatch();
  const additionalConfigData = useSelector(selectAdditionalConfigData);

  // Watch all form values to track changes
  const watchedValues = useWatch({ control });

  // Use the configRegistryData as provided - no filtering in this component
  const requiredConfigs = configRegistryData;

  const isMandatoryField = (cfg: ConfigRegistryConfig): boolean => isDynamicRegistryFieldMandatory(cfg);

  // Build a map for quick ref resolution
  const configRefMap = React.useMemo(() => {
    const map = new Map<string, { type: string; key: string }>();
    configRegistryData.forEach(ct => {
      ct.configs.forEach(cfg => {
        map.set(`${ct.configType}.${cfg.configKey}`, { type: ct.configType, key: cfg.configKey });
        map.set(cfg.configKey, { type: ct.configType, key: cfg.configKey });
      });
    });
    return map;
  }, [configRegistryData]);

  // Resolve a ref like "Ingestion.FuelType" or "FuelType" relative to a current type
  const resolveFieldRef = React.useCallback((ref: { configType?: string; configKey?: string }, currentType: string): string | null => {
    if (!ref) return null;
    const rt = ref.configType || currentType;
    const rk = ref.configKey;
    if (!rk) return null;
    const exact = configRefMap.get(`${rt}.${rk}`);
    if (exact) return `${exact.type}.${exact.key}`;
    // Case-insensitive fallback search to improve robustness
    const rtLc = String(rt).toLowerCase();
    const rkLc = String(rk).toLowerCase();
    let found: string | null = null;
    configRefMap.forEach((v) => {
      if (!found && String(v.type).toLowerCase() === rtLc && String(v.key).toLowerCase() === rkLc) {
        found = `${v.type}.${v.key}`;
      }
    });
    if (found) return found;
    // Ref has no explicit configType (e.g. FUEL_TYPE_DEP_REF) — it isn't scoped to currentType,
    // so match by configKey alone rather than requiring it live under the same type bucket.
    if (!ref.configType) {
      const anyType = configRefMap.get(rk);
      if (anyType) return `${anyType.type}.${anyType.key}`;
    }
    return null;
  }, [configRefMap]);

  // Get a human-friendly label for a field's current value
  const getSelectedLabel = React.useCallback((fieldName: string): string => {
    if (!fieldName) return '';
    const [type, key] = fieldName.split('.');
    const configType = configRegistryData.find(ct => ct.configType === type);
    const cfg = configType?.configs.find(c => c.configKey === key);
    const value = getValues ? getValues(fieldName) : undefined;
    if (value === undefined || value === null) return '';

    // Attempt to map value to a possible value label
    const parsePossibleValues = (pv: any): string[] => {
      if (!pv) return [];
      if (Array.isArray(pv)) {
        const result: string[] = [];
        pv.forEach(v => {
          if (v !== null && v !== undefined) {
            const s = String(v);
            const parts = s.split('\n').map(p => p.trim()).filter(Boolean);
            result.push(...parts);
          }
        });
        return result;
      }
      if (typeof pv === 'string') {
        try {
          const arr = JSON.parse(pv);
          if (Array.isArray(arr)) return arr.map(x => String(x));
        } catch {}
        return pv.split(',').map((x: string) => x.trim()).filter(Boolean);
      }
      if (typeof pv === 'object') {
        // Special case: map-shaped possibleValues used as multiselect of keys
        return Object.keys(pv);
      }
      return [];
    };

    const toTitle = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

    const options = parsePossibleValues(cfg?.possibleValues);
    const toLabel = (val: any) => {
      if (typeof val === 'boolean') return val ? 'On' : 'Off';
      const s = String(val);
      // If options contain the same casing, use it; else fallback to TitleCase for map-key scenario
      const match = options.find(o => o === s);
      return match || toTitle(s);
    };

    if (Array.isArray(value)) {
      return value.map(v => toLabel(v)).join(', ');
    }
    if (typeof value === 'object') {
      try { return JSON.stringify(value); } catch { return String(value); }
    }
    return toLabel(value);
  }, [configRegistryData, getValues]);

  // Type guard for structured dependency ref
  const isRegistryRef = (x: any): x is { configType?: string; configKey?: string } => {
    return x && typeof x === 'object' && 'configKey' in x;
  };

  // Compute display name with token substitution
  const computeTemplatedLabel = React.useCallback((config: ConfigRegistryConfig, currentType: string): string => {
    let label = config.displayName || '';
    if (!label || label.indexOf('${') === -1) return label;

    const depends = config.configDependsUpon as any;
    const tokenMap: Record<string, string> = {};
    let singleResolved: string | null = null;

    if (depends) {
      if (Array.isArray(depends)) {
        // If multiple, use the first for single-token fallback
        const first = depends[0];
        const resolved = resolveFieldRef(first as any, currentType);
        if (resolved) singleResolved = getSelectedLabel(resolved);
      } else if (isRegistryRef(depends)) {
        const resolved = resolveFieldRef(depends as any, currentType);
        if (resolved) singleResolved = getSelectedLabel(resolved);
      } else if (typeof depends === 'object') {
        Object.entries(depends).forEach(([token, ref]) => {
          const resolved = resolveFieldRef(ref as any, currentType);
          if (resolved) tokenMap[token] = resolved;
        });
      }
    }

    label = label.replace(/\$\{([^}]+)\}/g, (_m, token) => {
      const mappedRef = tokenMap[token];
      if (mappedRef) {
        const val = getSelectedLabel(mappedRef);
        return val || _m;
      }
      // Single dependency fallback: if only one ref provided, use its value for any token
      if (singleResolved) return singleResolved;
      return _m; // leave as-is
    });

    return label;
  }, [resolveFieldRef, getSelectedLabel]);

  // Build dependency graph once from configDependsUpon to enable easy nesting later
  const dependencyGraph = React.useMemo(() => {
    const parentToChildren = new Map<string, string[]>();
    const childToParents = new Map<string, string[]>();

    const addEdge = (parentField: string, childField: string) => {
      const children = parentToChildren.get(parentField) || [];
      if (!children.includes(childField)) parentToChildren.set(parentField, [...children, childField]);
      const parents = childToParents.get(childField) || [];
      if (!parents.includes(parentField)) childToParents.set(childField, [...parents, parentField]);
    };

    configRegistryData.forEach(ct => {
      ct.configs.forEach(cfg => {
        const childField = `${ct.configType}.${cfg.configKey}`;
        const depends = (cfg as any).configDependsUpon as any;
        if (!depends) return;
        let refs: any[] = [];
        if (Array.isArray(depends)) refs = depends;
        else if (isRegistryRef(depends)) refs = [depends];
        else if (typeof depends === 'object') refs = Object.values(depends);
        refs.forEach(ref => {
          const parentField = resolveFieldRef(ref as any, ct.configType);
          if (parentField) addEdge(parentField, childField);
        });
      });
    });

    return { parentToChildren, childToParents };
  }, [configRegistryData, resolveFieldRef]);

  // Determine if a config should be visible based on dependencies
  const shouldShow = React.useCallback((config: ConfigRegistryConfig, currentType: string): boolean => {
    const depends = (config as any).configDependsUpon as any;
    if (!depends) return true;

    let refs: any[] = [];
    if (Array.isArray(depends)) refs = depends;
    else if (isRegistryRef(depends)) refs = [depends];
    else if (typeof depends === 'object') refs = Object.values(depends);

    for (const r of refs) {
      // If it is a structured registry ref, prefer normalized selection set logic
      if (isRegistryRef(r)) {
        const selected = getSelectedDependencyKeys(r, currentType);
        if (selected.size > 0) return true;
      }
      const parentField = resolveFieldRef(r as any, currentType);
      if (!parentField || !getValues) continue;
      const val = getValues(parentField);
      if (Array.isArray(val)) {
        // Visible if any selection is made; empty array means hide
        if (val.length > 0) return true;
      } else if (typeof val === 'boolean') {
        if (val) return true;
      } else if (typeof val === 'string') {
        if (val.trim() !== '') return true;
      } else if (val && typeof val === 'object') {
        if (Object.keys(val).length > 0) return true;
      }
      // If the parent field exists but is currently empty, we conservatively hide.
    }
    return false;
  }, [getValues, resolveFieldRef]);

  // Function to save field value to Redux
  const saveFieldToRedux = (fieldName: string, value: any) => {
    // Ensure multiselect values are properly stored as arrays
    let finalValue = value;
    
    // Check if this field is a multiselect by looking at the config
    const [configType, configKey] = fieldName.split('.');
    const config = configRegistryData
      .find(ct => ct.configType === configType)
      ?.configs.find(c => c.configKey === configKey);
    
    if (isRegistryMultiselectArrayField(config, configType) && Array.isArray(value)) {
      // Ensure it's an array of strings
      finalValue = value.map(v => String(v));
    }

    dispatch(setAdditionalConfigData({ [fieldName]: finalValue }));
  };

  // Function to get form values with proper multiselect formatting
  // This ensures that multiselect fields always return arrays instead of comma-separated strings
  const getFormattedFormValues = React.useCallback(() => {
    if (!getValues) return {};
    
    const formValues = getValues();
    const formattedValues = { ...formValues };
    
    // Process each field to ensure multiselect values are arrays
    Object.keys(formattedValues).forEach(fieldName => {
      if (fieldName === 'configGroupSelection') return; // Skip this special field
      
      const [configType, configKey] = fieldName.split('.');
      const config = configRegistryData
        .find(ct => ct.configType === configType)
        ?.configs.find(c => c.configKey === configKey);
      
      if (isRegistryMultiselectArrayField(config, configType)) {
        const value = formattedValues[fieldName];
        if (value && !Array.isArray(value)) {
          // Convert non-array values to arrays for multiselect fields
          if (typeof value === 'string') {
            try {
              const parsed = JSON.parse(value);
              formattedValues[fieldName] = Array.isArray(parsed) ? parsed : [value];
            } catch (e) {
              formattedValues[fieldName] = [value];
            }
          } else {
            formattedValues[fieldName] = [String(value)];
          }
        }
      }
    });
    
    return formattedValues;
  }, [getValues, configRegistryData]);

  // Call the callback whenever form values change to ensure parent components get properly formatted values
  React.useEffect(() => {
    if (onFormValuesChange && getValues) {
      const formattedValues = getFormattedFormValues();
      onFormValuesChange(formattedValues);
    }
  }, [watchedValues, onFormValuesChange, getValues, getFormattedFormValues]);

  // Configs sorted by order, then narrowed to the selected data source.
  // Until a data source is picked, source-specific configs stay hidden.
  const getFilteredAndSortedConfigs = () =>
    filterConfigsByGroupSelection(requiredConfigs, configGroupSelection)
      .filter(configType => configType.configs.length > 0);

  // Set default values when config registry data changes
  React.useEffect(() => {
    if (configRegistryData.length > 0) {
      configRegistryData.forEach((configType) => {
        configType.configs.forEach((config) => {
          const fieldName = `${configType.configType}.${config.configKey}`;
          let defaultValue: any = '';
          
          // Check if this is a map structure
          const isMapStructure = checkIfMapStructure(config.possibleValues);
          
          if (isBooleanRenderType(config.renderType)) {
            defaultValue = config.value === 'true' || config.value === 'TRUE';
          } else if (isMapStructure) {
            // For map multiselect, try to parse existing value or default to empty object
            if (config.value) {
              try {
                let parsed: any = {};
                if(typeof config.value === 'string') {
                  parsed = JSON.parse(config.value);
                } else {
                  parsed = config.value;
                }
                if (typeof parsed === 'object' && !Array.isArray(parsed)) {
                  defaultValue = parsed;
                } 
              } catch (e) {
                defaultValue = {};
              }
            } else {
              defaultValue = {};
            }
          } else if (config.value) {
            defaultValue = String(config.value);
          } else {
            defaultValue = '';
          }
          
          setValue(fieldName, defaultValue);
        });
      });
    }
  }, [configRegistryData, setValue]);

  // Load saved values from Redux
  React.useEffect(() => {
    if (Object.keys(additionalConfigData).length > 0) {
      Object.entries(additionalConfigData).forEach(([fieldName, value]) => {
        if (value !== undefined && value !== null) {
          // Ensure multiselect values are properly restored as arrays
          let finalValue = value;
          
          // Check if this field is a multiselect by looking at the config
          const [configType, configKey] = fieldName.split('.');
          const config = configRegistryData
            .find(ct => ct.configType === configType)
            ?.configs.find(c => c.configKey === configKey);
          
          if (isRegistryMultiselectArrayField(config, configType)) {
            // If it's a multiselect field, ensure the value is an array
            if (typeof value === 'string') {
              // If it's a string, try to parse it as JSON array, otherwise split by comma
              try {
                const parsed = JSON.parse(value);
                finalValue = Array.isArray(parsed) ? parsed : [value];
              } catch (e) {
                // If parsing fails, split by comma and trim
                finalValue = value.split(',').map(v => v.trim()).filter(v => v);
              }
            } else if (!Array.isArray(value)) {
              // If it's not an array, convert to single item array
              finalValue = [String(value)];
            }
          }
          
          setValue(fieldName, finalValue);
        }
      });
    }
  }, [additionalConfigData, setValue, configRegistryData]);

  // Strict case-insensitive equality (framework-agnostic)
  const equalsIgnoreCase = (a: string, b: string) => String(a).trim().toLowerCase() === String(b).trim().toLowerCase(); 
  // Helper function to parse possibleValues
  // Match selected fuel label to registry key using equalsIgnoreCase, with minimal synonyms
  const matchesFuelKey = (selected: string, key: string): boolean => {
    if (equalsIgnoreCase(selected, key)) return true;
    if (key === 'ELECTRIC' && (equalsIgnoreCase(selected, 'Electricity') || equalsIgnoreCase(selected, 'Electric'))) return true;
    if (key === 'GAS' && equalsIgnoreCase(selected, 'Gas')) return true;
    if (key === 'WATER' && equalsIgnoreCase(selected, 'Water')) return true;
    return false;
  };

  // Return a normalized Set of selected keys (e.g., ELECTRIC/GAS/WATER) for a dependency ref
  const getSelectedDependencyKeys = (depRef: any, currentType: string): Set<string> => {
    const set = new Set<string>();
    const parentRef = resolveFieldRef(depRef as any, currentType);
    if (!parentRef) return set;
    const fromForm = getValues ? getValues(parentRef) : undefined;
    let parentVal: any = fromForm;
    if ((Array.isArray(parentVal) && parentVal.length === 0) || parentVal === '' || parentVal == null) {
      parentVal = (additionalConfigData as any)?.[parentRef];
    }
    if (Array.isArray(parentVal)) {
      parentVal.forEach(v => {
        const s = String(v);
        if (matchesFuelKey(s, 'ELECTRIC')) set.add('ELECTRIC');
        if (matchesFuelKey(s, 'GAS')) set.add('GAS');
        if (matchesFuelKey(s, 'WATER')) set.add('WATER');
      });
    } else if (typeof parentVal === 'string' && parentVal) {
      const s = parentVal;
      if (matchesFuelKey(s, 'ELECTRIC')) set.add('ELECTRIC');
      if (matchesFuelKey(s, 'GAS')) set.add('GAS');
      if (matchesFuelKey(s, 'WATER')) set.add('WATER');
    }
    return set;
  };

  const renderField = (config: ConfigRegistryConfig, configType: string) => {
    const fieldName = `${configType}.${config.configKey}`;
    // Access nested error structure
    const fieldError = (errors as any)[configType]?.[config.configKey];
    
    const parsedValues = parsePossibleValues(config.possibleValues);
    const isMapStructure = parsedValues.type === 'map';
    const possibleValuesArray = parsedValues.type === 'simple' ? parsedValues.data : [];
    const mapData = parsedValues.type === 'map' ? parsedValues.data : {};
    
    // Debug logging
    
    
    const hasPossibleValues = possibleValuesArray.length > 0 || isMapStructure;
    
    // Render mode comes from renderType; map vs flat still comes from possibleValues shape
    const isMulti = isMultiSelectRenderType(config?.renderType) || isFrontendSupportedLocaleField(config, configType);
    const isMapMultiselect = isMapStructure; // Map structures become multiselect
    const isSelect = hasPossibleValues && !isMapStructure; // Simple possibleValues become dropdown
    const isBoolean = !hasPossibleValues && isBooleanRenderType(config.renderType);
    const isMapKeysMultiSelect = isMapStructure && isMulti;
    
    // Compute dependency-based label and visibility
    const computedLabel = computeTemplatedLabel(config, configType) || config.displayName;
    const visible = shouldShow(config, configType);
    const isDependent = Boolean(config.configDependsUpon);

    if (!visible) {
      return null;
    }

    // Set default value based on field type
    let defaultValue: any = '';
    if (isBoolean) {
      defaultValue = config.value === 'true' || config.value === 'TRUE';
    } else if (isMapKeysMultiSelect) {
      // For map of options rendered as checkbox multiselect, default to []
      if (Array.isArray(config.value)) {
        defaultValue = config.value.map(v => String(v));
      } else if (typeof config.value === 'string') {
        try {
          const parsed = JSON.parse(config.value);
          defaultValue = Array.isArray(parsed) ? parsed.map(v => String(v)) : [];
        } catch (e) {
          defaultValue = config.value ? [String(config.value)] : [];
        }
      } else {
        defaultValue = [];
      }
    } else if (isMapMultiselect) {
      // For map multiselect, default to empty object
      defaultValue = {};
    } else if (isSelect) {
      // For select fields, handle multiselect vs single select
      if (isMulti) {
        if (config.value !== undefined && config.value !== null && config.value !== '') {
          if (Array.isArray(config.value)) {
            defaultValue = config.value.map(v => String(v));
          } else if (typeof config.value === 'string') {
            try {
              const parsed = JSON.parse(config.value);
              defaultValue = Array.isArray(parsed) ? parsed.map(v => String(v)) : [String(config.value)];
            } catch (e) {
              defaultValue = [String(config.value)];
            }
          } else {
            defaultValue = [String(config.value)];
          }
        } else {
          defaultValue = [];
        }
      } else if (config.value !== undefined && config.value !== null && config.value !== '') {
        defaultValue = String(config.value);
      } else {
        defaultValue = '';
      }
    } else {
      defaultValue = config.value || '';
    }

    // A helper to render label row using computedLabel
    const renderLabelRow = (doc?: string, required?: boolean) => (
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 500, textAlign: 'left', fontFamily: 'inherit' }}>
          {computedLabel}
          {required && (
            <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>
          )}
        </Typography>
        {doc && (
          <Tooltip title={doc} arrow placement="top">
            <IconButton size="small" sx={{ p: 0.25 }}>
              <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );

    // Wrapper to optionally apply dependency styling
    const wrapIfDependent = (node: React.ReactNode) => (
      isDependent ? (
        <Box sx={{ ml: 3, pl: 3, borderLeft: '2px dashed', borderColor: 'primary.main' }}>
          {node}
        </Box>
      ) : (
        <>{node}</>
      )
    );

    // Answered on an earlier step, or DA-2161 marks this config view-only for the
    // caller's role: show the value, keep it uneditable. Still rendered (not hidden)
    // so dependent fields below it have visible context. ('hidden' configs never
    // reach here -- proxypulse drops them from the registry response entirely.)
    if (readOnlyConfigKeys?.includes(config.configKey) || config.accessLevel === 'view') {
      const selectedLabel = getSelectedLabel(fieldName);
      const chipLabels = selectedLabel ? selectedLabel.split(',').map(s => s.trim()).filter(Boolean) : [];
      return wrapIfDependent(
        <FormControl fullWidth>
          {!isDependent && renderLabelRow(config.configDocumentation, false)}
          {chipLabels.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, py: 0.5 }}>
              {chipLabels.map((label) => (
                <Chip sx={{ fontWeight: 600 }} key={label} label={label} size="medium" />
              ))}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ textAlign: 'left', py: 1, color: 'text.secondary' }}>
              —
            </Typography>
          )}
        </FormControl>
      );
    }

    // Map keys as checkbox multiselect
    if (isMapKeysMultiSelect) {
      const mapKeys = Object.keys(mapData || {});
      return wrapIfDependent(
        <Controller
          key={fieldName}
          name={fieldName}
          control={control}
          defaultValue={defaultValue}
          rules={{
            validate: (value) => {
              if (!isMandatoryField(config)) return true;
              return (Array.isArray(value) && value.length > 0) || 'This field is required.';
            }
          }}
          render={({ field }) => (
            <FormControl error={!!fieldError} fullWidth>
              {!isDependent && renderLabelRow(config.configDocumentation, isMandatoryField(config))}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 1, pl: 0.5 }}>
                {mapKeys.map((option: string) => {
                  const currentValues: string[] = Array.isArray(field.value) ? field.value : [];
                  const checked = currentValues.includes(option);
                  return (
                    <FormControlLabel
                      key={option}
                      control={
                        <StyledCheckbox
                          checked={checked}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            let nextValues: string[];
                            if (isChecked) {
                              nextValues = Array.from(new Set([...(currentValues || []), option]));
                            } else {
                              nextValues = (currentValues || []).filter(v => v !== option);
                            }
                            field.onChange(nextValues);
                            saveFieldToRedux(fieldName, nextValues);
                          }}
                        />
                      }
                      label={option}
                      sx={{ alignItems: 'center', m: 0, mr: 2, mb: 1 }}
                    />
                  );
                })}
              </Box>
              <FormHelperText error={!!fieldError} sx={{ textAlign: 'left', fontFamily: 'inherit' }}>
                {fieldError?.message}
              </FormHelperText>
            </FormControl>
          )}
        />
      );
    }
 
    // Map multiselect component
    if (isMapMultiselect) {
      // Determine which keys to show from dependency selection, if present
      const depends = config.configDependsUpon;
      const mapKeys = Object.keys(mapData || {});
      let keysToRender: string[] = [];
      if (depends || isFuelKeyedMap(mapKeys)) {
        // Fuel-keyed maps fall back to the fuel-type field when the registry sends no dependency
        const depRef = !depends
          ? FUEL_TYPE_DEP_REF
          : Array.isArray(depends)
            ? depends[0]
            : (isRegistryRef(depends) ? depends : Object.values(depends as any)[0]);
        const selectedKeys = getSelectedDependencyKeys(depRef, configType);
        // selectedKeys is always upper-cased (ELECTRIC/GAS/WATER); map keys may come from the
        // registry in any case, so normalize before matching or a fuel-keyed section silently vanishes.
        keysToRender = mapKeys.filter(k => selectedKeys.has(String(k).toUpperCase()));
        // If nothing is selected in parent, hide entire section
        if (keysToRender.length === 0) {
          return null;
        }
      } else {
        // Not fuel-keyed and no dependency specified; render everything
        keysToRender = mapKeys;
      }
      console.debug('[DynamicConfigForm] Dep map keysToRender:', keysToRender);
 
      return (
        <Controller
          key={fieldName}
          name={fieldName}
          control={control}
          defaultValue={defaultValue}
          rules={{
            validate: (value) => {
              if (!isMandatoryField(config)) return true;
              if (!keysToRender || keysToRender.length === 0) return true; // nothing visible
              const allSelected = keysToRender.every((k) => value && String((value as any)[k] || '').trim() !== '');
              return allSelected || 'Please select an option for all visible sections.';
            }
          }}
          render={({ field }) => (
            <FormControl error={!!fieldError} fullWidth>
              {keysToRender.map((key, index) => {
                  const allowed = (mapData?.[key] as string[]) || [];
                  // const helper = allowed.length > 0 ? `${allowed.join(', ')}` : undefined;
                  const currentValue = field.value?.[key] || '';
                  const fuelLabel = key.charAt(0) + key.slice(1).toLowerCase();
                  const baseDisplay = config.displayName || key;
                  const sectionLabel = baseDisplay.replace(/\$\{FuelType\}/g, fuelLabel);
                  return (
                    <Box key={key} sx={{ display: 'flex', alignItems: 'stretch', gap: 2, mb: 2, ml: 4 }}>
                      <Box sx={{ width: 10, ml: 0.5, borderLeft: '2px dashed', borderColor: 'primary.main', opacity: 0.35 }} />
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500, textAlign: 'left' }}>
                            {sectionLabel}
                          </Typography>
                          {isMandatoryField(config) && (
                            <Box component="span" sx={{ color: 'error.main' }}>*</Box>
                          )}
                          {config.configDocumentation && (
                            <Tooltip title={config.configDocumentation} arrow placement="top">
                              <IconButton size="small" sx={{ p: 0.25 }}>
                                <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        <FormControl fullWidth size="small">
                          <Select
                            value={currentValue}
                            displayEmpty
                            onChange={(e) => {
                              const newValue = e.target.value as string;
                              const updated = { ...(field.value || {}), [key]: newValue };
                              field.onChange(updated);
                              saveFieldToRedux(fieldName, updated);
                            }}
                            renderValue={(selected) =>
                              selected || (
                                <Typography variant="body2" color="text.secondary">
                                  Select an option
                                </Typography>
                              )
                            }
                            sx={{ '& .MuiSelect-select': { textAlign: 'left', fontFamily: 'inherit', py: 1 } }}
                          >
                            <MenuItem value="">
                              <Typography variant="body2" color="text.secondary">Select an option</Typography>
                            </MenuItem>
                            {allowed.map((opt) => (
                              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>
                  );
                })}
              <FormHelperText error={!!fieldError} sx={{ textAlign: 'left', fontFamily: 'inherit', mt: 1 }}>
                {fieldError?.message}
              </FormHelperText>
            </FormControl>
          )}
        />
      );
    }

    if (isBoolean) {
      return wrapIfDependent(
        <Controller
          key={fieldName}
          name={fieldName}
          control={control}
          defaultValue={defaultValue}
          render={({ field }) => (
            <FormControl error={!!fieldError} fullWidth>
              {!isDependent && renderLabelRow(config.configDocumentation, isMandatoryField(config))}
              <FormControlLabel
                control={
                  <StyledCheckbox
                    checked={field.value || false}
                    onChange={(e) => {
                      const newValue = e.target.checked;
                      field.onChange(newValue);
                      // Save to Redux
                      saveFieldToRedux(fieldName, newValue);
                    }}
                  />
                }
                label=""
              />
              <FormHelperText error={!!fieldError} sx={{ textAlign: 'left', fontFamily: 'inherit' }}>
                {fieldError?.message}
              </FormHelperText>
            </FormControl>
          )}
        />
      );
    }

    if (isSelect) {
      return wrapIfDependent(
        <Controller
          key={fieldName}
          name={fieldName}
          control={control}
          defaultValue={defaultValue}
          rules={{
            validate: (value) => {
              if (!isMandatoryField(config)) return true;
              if (isMulti) {
                return (Array.isArray(value) && value.length > 0) || 'This field is required.';
              }
              return (typeof value === 'string' && value.trim() !== '') || 'This field is required.';
            }
          }}
          render={({ field }) => (
            <FormControl error={!!fieldError} fullWidth>
              {!isDependent && renderLabelRow(config.configDocumentation, isMandatoryField(config))}

              {isMulti ? (
                isFrontendSupportedLocaleField(config, configType) ? (
                  <SearchableMultiSelect
                    label={computedLabel}
                    options={possibleValuesArray}
                    selected={Array.isArray(field.value) ? field.value : []}
                    onChange={(next) => {
                      field.onChange(next);
                      saveFieldToRedux(fieldName, next);
                    }}
                    placeholder="Select options"
                    hideLabel
                  />
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 1, pl: 0.5 }}>
                    {possibleValuesArray.map((option: string) => {
                      const currentValues: string[] = Array.isArray(field.value) ? field.value : [];
                      const checked = currentValues.includes(option);
                      return (
                        <FormControlLabel
                          key={option}
                          control={
                            <StyledCheckbox
                              checked={checked}
                              onChange={(e) => {
                                const isChecked = e.target.checked;
                                let nextValues: string[];
                                if (isChecked) {
                                  nextValues = Array.from(new Set([...(currentValues || []), option]));
                                } else {
                                  nextValues = (currentValues || []).filter(v => v !== option);
                                }
                                field.onChange(nextValues);
                                saveFieldToRedux(fieldName, nextValues);
                              }}
                            />
                          }
                          label={option}
                          sx={{ alignItems: 'center', m: 0, mr: 2, mb: 1 }}
                        />
                      );
                    })}
                  </Box>
                )
              ) : (
                <SearchableMultiSelect
                  label={computedLabel}
                  options={possibleValuesArray}
                  selected={field.value ? [String(field.value)] : []}
                  onChange={(vals) => {
                    const newValue = vals[0] ?? '';
                    field.onChange(newValue);
                    saveFieldToRedux(fieldName, newValue);
                  }}
                  placeholder="Select an option"
                  singleSelect
                  hideLabel
                />
              )}

              <FormHelperText error={!!fieldError} sx={{ textAlign: 'left', fontFamily: 'inherit' }}>
                {fieldError?.message}
              </FormHelperText>
            </FormControl>
          )}
        />
      );
    }

    // Default to text input
    return wrapIfDependent(
      <Controller
        key={fieldName}
        name={fieldName}
        control={control}
        defaultValue={defaultValue}
        rules={{
          required: isMandatoryField(config) ? 'This field is required.' : false,
        }}
        render={({ field }) => (
          <FormControl error={!!fieldError} fullWidth>
            {!isDependent && renderLabelRow(config.configDocumentation, isMandatoryField(config))}
            <TextField
              {...field}
              fullWidth
              error={!!fieldError}
              helperText={fieldError?.message}
              placeholder={config.configDocumentation}
              required={isMandatoryField(config)}
              value={field.value || ''}
              onChange={(e) => {
                const newValue = e.target.value;
                field.onChange(newValue);
                // Save to Redux
                saveFieldToRedux(fieldName, newValue);
              }}
              size="small"
              sx={{
                '& .MuiInputBase-input': {
                  textAlign: 'left',
                  padding:1,
                },
                '& .MuiInputBase-input::placeholder': {
                  textAlign: 'left',
                },
                '& .MuiFormHelperText-root': {
                  textAlign: 'left',
                },
              }}
            />
          </FormControl>
        )}
      />
    );
  };

  if (requiredConfigs.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No configuration fields are required for this project.
        </Typography>
      </Box>
    );
  }



  // Get filtered and sorted configs
  const filteredConfigs = getFilteredAndSortedConfigs();
  
  // Dependency-aware flattening: render dependents immediately after their master
  const allConfigs = (() => {
    type Cfg = ConfigRegistryConfig & { configType: string };
    const flat: Cfg[] = [];
    const byField = new Map<string, Cfg>();
    const rendered = new Set<string>();

    // Build lookup map for quick access by field name
    filteredConfigs.forEach(fc => {
      fc.configs.forEach(fcfg => {
        const fieldName = `${fc.configType}.${fcfg.configKey}`;
        const withType = { ...(fcfg as any), configType: fc.configType } as Cfg;
        byField.set(fieldName, withType);
      });
    });

    // Base order: the hardcoded display order, applied across all configTypes.
    // Stable sort, so configs absent from CONFIG_DISPLAY_ORDER keep registry order at the end.
    const baseOrder: string[] = [];
    filteredConfigs.forEach(fc => {
      fc.configs.forEach(fcfg => {
        baseOrder.push(`${fc.configType}.${fcfg.configKey}`);
      });
    });
    baseOrder.sort(
      (a, b) => configDisplayRank(byField.get(a)?.configKey || '') - configDisplayRank(byField.get(b)?.configKey || '')
    );

    // Dependency-aware DFS to ensure parents render before dependents
    const visiting = new Set<string>();
    const visit = (field: string) => {
      if (rendered.has(field) || visiting.has(field)) return;
      visiting.add(field);

      // Ensure parents (within current filtered set) render first
      const parents = dependencyGraph.childToParents.get(field) || [];
      parents.forEach(parentField => {
        if (byField.has(parentField)) visit(parentField);
      });

      // Render current
      const node = byField.get(field);
      if (node && !rendered.has(field)) {
        flat.push(node);
        rendered.add(field);
      }

      // Then render children (within current filtered set)
      const childrenFields = dependencyGraph.parentToChildren.get(field) || [];
      childrenFields.forEach(childField => {
        if (byField.has(childField)) visit(childField);
      });

      visiting.delete(field);
    };

    baseOrder.forEach(f => visit(f));

    // Append any remaining configs (defensive)
    byField.forEach((cfg, field) => {
      if (!rendered.has(field)) flat.push(cfg);
    });

    return flat;
  })() as Array<ConfigRegistryConfig & { configType: string }>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Config Fields - One per line */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {allConfigs.map((config) => (
          <Box key={`${config.configType}.${config.configKey}`}>
            {renderField(config, config.configType)}
          </Box>
        ))}
      </Box>
    </Box>
  );
};